import React, { useState } from "react"
import { useNavigate } from "@tanstack/react-router"
import { Card, Steps, Space, Button, Typography } from "@mimber/ui-web"
import type { OrganizationDto } from "@mimber/schema"
import { setSelectedOrganizationId } from "@/utils/organization"
import { CreateOrganizationStep } from "./steps/CreateOrganizationStep"
import { CreateMembershipTierStep } from "./steps/CreateMembershipTierStep"
import { CreateOrganizationRoleStep } from "./steps/CreateOrganizationRoleStep"
import { CreateGroupStep } from "./steps/CreateGroupStep"
import { InviteFirstMemberStep } from "./steps/InviteFirstMemberStep"
import { BillingDetailsStep } from "./steps/BillingDetailsStep"
import { WizardSummaryStep } from "./WizardSummaryStep"
import type {
  OrganizationCreationWizardProps,
  WizardStepConfig,
  WizardStepKey,
  WizardStepStatus,
} from "./OrganizationCreationWizard.types"

const { Title, Paragraph } = Typography

const STEP_CONFIG: WizardStepConfig[] = [
  {
    key: "organization",
    title: "Create Organization",
    description: "Set up your organization's basic profile.",
    skippable: false,
  },
  {
    key: "membershipTier",
    title: "Membership Tier",
    description: "Create your first membership tier.",
    skippable: true,
  },
  {
    key: "role",
    title: "Organization Role",
    description: "Create a custom role for your leaders.",
    skippable: true,
  },
  {
    key: "group",
    title: "Group / Committee",
    description: "Create your first group or committee.",
    skippable: true,
  },
  {
    key: "member",
    title: "Invite First Member",
    description: "Add or invite your first member.",
    skippable: true,
  },
  {
    key: "billing",
    title: "Billing Details",
    description: "Enter your organization's billing details.",
    skippable: true,
  },
]

const INITIAL_STEP_STATUSES: Record<WizardStepKey, WizardStepStatus> = {
  organization: "pending",
  membershipTier: "pending",
  role: "pending",
  group: "pending",
  member: "pending",
  billing: "pending",
}

export const OrganizationCreationWizard: React.FunctionComponent<
  OrganizationCreationWizardProps
> = ({ onComplete }) => {
  const navigate = useNavigate()
  const [currentStepIndex, setCurrentStepIndex] = useState(0)
  const [isSummary, setIsSummary] = useState(false)
  const [stepStatuses, setStepStatuses] = useState<
    Record<WizardStepKey, WizardStepStatus>
  >(INITIAL_STEP_STATUSES)
  const [organization, setOrganization] = useState<OrganizationDto | null>(null)

  const currentStep = STEP_CONFIG[currentStepIndex]

  const markStatus = (key: WizardStepKey, status: WizardStepStatus) => {
    setStepStatuses((previous) => ({ ...previous, [key]: status }))
  }

  const goToNextStep = () => {
    if (currentStepIndex === STEP_CONFIG.length - 1) {
      setIsSummary(true)
      return
    }
    setCurrentStepIndex((previous) => previous + 1)
  }

  const goToPreviousStep = () => {
    if (isSummary) {
      setIsSummary(false)
      return
    }
    setCurrentStepIndex((previous) => Math.max(previous - 1, 0))
  }

  const handleStepSuccess = (key: WizardStepKey) => {
    markStatus(key, "completed")
    goToNextStep()
  }

  const handleOrganizationCreated = (createdOrganization: OrganizationDto) => {
    setOrganization(createdOrganization)
    handleStepSuccess("organization")
  }

  const handleSkip = () => {
    markStatus(currentStep.key, "skipped")
    goToNextStep()
  }

  const handleGoToDashboard = () => {
    if (organization) {
      setSelectedOrganizationId(organization.uuid)
    }
    onComplete?.()
    navigate({ to: "/" })
  }

  if (isSummary) {
    return (
      <WizardSummaryStep
        stepConfig={STEP_CONFIG}
        stepStatuses={stepStatuses}
        onGoToDashboard={handleGoToDashboard}
      />
    )
  }

  const renderStepContent = () => {
    switch (currentStep.key) {
      case "organization":
        return <CreateOrganizationStep onSuccess={handleOrganizationCreated} />
      case "membershipTier":
        return (
          <CreateMembershipTierStep
            onSuccess={() => handleStepSuccess("membershipTier")}
          />
        )
      case "role":
        return (
          <CreateOrganizationRoleStep
            onSuccess={() => handleStepSuccess("role")}
          />
        )
      case "group":
        return <CreateGroupStep onSuccess={() => handleStepSuccess("group")} />
      case "member":
        return (
          <InviteFirstMemberStep
            onSuccess={() => handleStepSuccess("member")}
          />
        )
      case "billing":
        if (!organization) {
          return null
        }
        return (
          <BillingDetailsStep
            organization={organization}
            onSuccess={() => handleStepSuccess("billing")}
          />
        )
      default:
        return null
    }
  }

  return (
    <Card>
      <Steps
        current={currentStepIndex}
        items={STEP_CONFIG.map((step) => ({
          title: step.title,
        }))}
        style={{ marginBottom: 32 }}
      />
      <Title level={4}>{currentStep.title}</Title>
      <Paragraph type="secondary">{currentStep.description}</Paragraph>
      {renderStepContent()}
      <Space style={{ marginTop: 24 }}>
        {currentStepIndex > 0 && (
          <Button onClick={goToPreviousStep}>Back</Button>
        )}
        {currentStep.skippable && <Button onClick={handleSkip}>Skip</Button>}
      </Space>
    </Card>
  )
}
